import React, { useState, useEffect } from 'react';
import { CheckCircle2 } from "lucide-react";

// Helper: Truncate text for table cells
const truncate = (str, max = 60) => (str && str.length > max ? str.slice(0, max) + "..." : str);

const COMPLIMENTS_API_URL = 'https://portal.lsb.go.ke/lsb/compliments/';

const ComplimentsTab = () => {
    const [compliments, setCompliments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState('');
    const [viewModal, setViewModal] = useState(false);
    const [selectedCompliment, setSelectedCompliment] = useState(null);
    const [lastRefresh, setLastRefresh] = useState(Date.now());

    // Fetch Compliments (GET only)
    const fetchCompliments = async () => {
        const accessToken = localStorage.getItem('accessToken');
        setLoading(true);
        setFetchError('');
        try {
            const res = await fetch(COMPLIMENTS_API_URL, {
                method: 'GET',
                headers: {
                    'Authorization': accessToken ? `Bearer ${accessToken}` : '',
                }
            });
            if (!res.ok) throw new Error("Failed to fetch compliments");
            const data = await res.json();
            const rawCompliments = Array.isArray(data) ? data : (data?.results || []);
            // Normalize
            const normalized = rawCompliments.map(n => ({
                ...n,
                compliment_id: n.compliment_id || n.id,
                licensed_val:
                    typeof n.licensed !== 'undefined' && n.licensed !== ''
                        ? (String(n.licensed) === 'true' ? 'Yes' : (String(n.licensed) === 'false' ? 'No' : n.licensed))
                        : 'N/A'
            }));
            setCompliments(normalized);
            setLastRefresh(Date.now());
        } catch (err) {
            setFetchError(err.message || "Unable to fetch compliments.");
        } finally {
            setLoading(false);
        }
    };

    // Set up auto-refresh every 15s
    useEffect(() => {
        fetchCompliments();
        const interval = setInterval(() => {
            fetchCompliments();
        }, 15000);

        return () => clearInterval(interval);
        // eslint-disable-next-line
    }, []);

    const openViewModal = (compliment) => {
        setSelectedCompliment(compliment);
        setViewModal(true);
    };

    const closeViewModal = () => {
        setSelectedCompliment(null);
        setViewModal(false);
    };

    return (
        <div className="max-w-7xl mx-auto">
            <div className="pb-3 flex flex-col justify-end text-xs leading-2 font-medium">
                <span>Auto-refresh: every 15s</span>
                <span className="" title={new Date(lastRefresh).toLocaleString()}>
                    Last refreshed: {new Date(lastRefresh).toLocaleTimeString()}
                </span>
            </div>

            {/* Error display */}
            {fetchError && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded mb-4">
                    {fetchError}
                </div>
            )}

            {/* Modal for viewing a compliment - Styled like Complaints Modal */}
            {viewModal && selectedCompliment && (
                <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm transition-all">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-0 overflow-hidden relative animate-in fade-in zoom-in duration-200">
                        {/* Header with Close Button */}
                        <div className='bg-[#3d0707] p-6 text-white flex justify-between items-center'>
                            <h3 className="text-xl font-bold flex items-center gap-2">
                                <CheckCircle2 className="w-6 h-6" /> Compliment Details
                            </h3>
                            <button
                                className="text-white/80 hover:text-white transition-colors group"
                                onClick={closeViewModal}
                                aria-label="Close"
                            >
                                <i className="fa-solid fa-xmark text-2xl transition-transform duration-200 group-hover:scale-125 group-hover:rotate-90" />
                            </button>
                        </div>

                        {/* Body - styled content */}
                        <div className="p-8">
                            <div className="flex gap-1 items-center">
                                <p className="text-sm font-bold uppercase tracking-wider text-gray-500">Compliment ID:</p>
                                <p className="font-bold capitalize">{selectedCompliment.compliment_id}</p>
                            </div>

                            <div className="flex gap-1 items-center">
                                <p className="text-sm font-bold uppercase tracking-wider text-gray-500">Licensed Personnel:</p>
                                <p className="font-bold capitalize">{selectedCompliment.licensed_val}</p>
                            </div>

                            <div className="flex gap-1 items-center">
                                <p className="text-sm font-bold uppercase tracking-wider text-gray-500">Subject / Title:</p>
                                <p className="font-bold capitalize">{selectedCompliment.title || selectedCompliment.subject || "No Subject"}</p>
                            </div>

                            <div className="flex gap-1 items-center">
                                <p className="text-sm font-bold uppercase tracking-wider text-gray-500">County:</p>
                                <p className="font-bold capitalize">{selectedCompliment.county || "Not Specified"}</p>
                            </div>

                            <div className="flex gap-1 items-center">
                                <p className="text-sm font-bold uppercase tracking-wider text-gray-500">Sub-County:</p>
                                <p className="font-bold capitalize">{selectedCompliment.sub_county || "Not Specified"}</p>
                            </div>

                            <div className="flex gap-1 items-center">
                                <p className="text-sm font-bold uppercase tracking-wider text-gray-500">Message:</p>
                                <p className="font-bold capitalize break-words">{selectedCompliment.message}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="py-2 px-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">ID</th>
                                <th className="py-2 px-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Licensed</th>
                                <th className="py-2 px-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Title / Subject</th>
                                <th className="py-2 px-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">County</th>
                                <th className="py-2 px-4 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan={5} className="py-20 text-center">
                                        <div className="flex flex-col items-center">
                                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#3d0707] mb-4"></div>
                                            <p className="text-gray-500 font-medium">Fetching compliments...</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : compliments.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="py-20 text-center">
                                        <div className="text-gray-400">
                                            <CheckCircle2 className="w-12 h-12 mx-auto mb-2 opacity-20" />
                                            <p>No compliments found in the system.</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                compliments.map(n => (
                                    <tr key={n.compliment_id || n.id} className="hover:bg-slate-50/50 transition-colors group">
                                        <td className="py-2 px-4 whitespace-nowrap">
                                            <span className="font-mono text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                                {truncate(String(n.compliment_id || n.id), 8)}
                                            </span>
                                        </td>
                                        <td className="py-2 px-4 font-medium text-gray-900">{n.licensed_val}</td>
                                        <td className="py-2 px-4 text-gray-600 capitalize">{truncate(n.title || n.subject, 40)}</td>
                                        <td className="py-2 px-4 text-gray-600 capitalize">{n.county || "N/A"}</td>
                                        <td className="py-2 px-4 text-center">
                                            <button
                                                onClick={() => openViewModal(n)}
                                                className="px-4 py-1.5 bg-indigo-50 text-indigo-600 rounded-lg text-sm font-bold hover:bg-indigo-100 transition shadow-sm"
                                                type="button"
                                            >
                                                View
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ComplimentsTab;
